import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { searchRequesters, createOrGetRequester, deleteRequester } from '../../services/logistics/requesters';
import { normalizeName } from '../../utils/text-utils';
import toast from 'react-hot-toast';

const ServiceRequesterCombobox = ({ 
  clientId, 
  value, 
  onChange, 
  disabled = false,
  required = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [cache, setCache] = useState(new Map());
  
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  // Update search query when value changes (only if different)
  useEffect(() => {
    if (value !== searchQuery) {
      setSearchQuery(value || '');
    }
  }, [value]); // Removed searchQuery from dependencies to avoid infinite loop

  // Clear field and cache when client changes
  const prevClientIdRef = useRef(clientId);
  useEffect(() => {
    if (prevClientIdRef.current !== clientId) {
      setSearchQuery('');
      setOptions([]);
      setCache(new Map());
      if (onChange) {
        onChange('');
      }
      prevClientIdRef.current = clientId;
    }
  }, [clientId, onChange]);

  // Debounced search function
  const debouncedSearch = useCallback(async (query) => {
    if (!clientId) return;

    // Check cache first
    const cacheKey = `${clientId}-${query}`;
    if (cache.has(cacheKey)) {
      setOptions(cache.get(cacheKey));
      return;
    }

    setLoading(true);
    try {
      const results = await searchRequesters(clientId, query, 10);
      setOptions(results);
      
      // Update cache (keep last 20 entries per client)
      const newCache = new Map(cache);
      if (newCache.size >= 20) {
        const firstKey = newCache.keys().next().value;
        newCache.delete(firstKey);
      }
      newCache.set(cacheKey, results);
      setCache(newCache);
    } catch (error) {
      console.error('Error searching requesters:', error);
      toast.error(t('errors.search_failed', 'Search failed'));
    } finally {
      setLoading(false);
    }
  }, [clientId, cache, t]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setHighlightedIndex(-1);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search by 200ms
    debounceRef.current = setTimeout(() => {
      debouncedSearch(query);
      // Also update parent after user stops typing
      if (onChange && query !== value) {
        onChange(query);
      }
    }, 200);
  };

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!searchQuery && clientId) {
      debouncedSearch('');
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    // Update parent with current search query
    if (onChange && searchQuery !== value) {
      onChange(searchQuery);
    }
    
    // Delay closing to allow clicking on options
    setTimeout(() => {
      if (!listRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 150);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        if (!searchQuery && clientId) {
          debouncedSearch('');
        }
      }
      return;
    }

    const totalOptions = options.length + (shouldShowAddOption() ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < options.length) {
            selectOption(options[highlightedIndex]);
          } else {
            handleCreateNew();
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Check if we should show "Add new" option
  const shouldShowAddOption = () => {
    if (!searchQuery.trim()) return false;
    
    const normalizedQuery = normalizeName(searchQuery);
    if (normalizedQuery.length < 2) return false;
    
    // Check if exact match exists
    return !options.some(option => 
      normalizeName(option.name) === normalizedQuery
    );
  };

  // Select existing option
  const selectOption = (option) => {
    setSearchQuery(option.name);
    setIsOpen(false);
    if (onChange) {
      onChange(option.name);
    }
  };

  // Handle creating new requester
  const handleCreateNew = async () => {
    if (!clientId || !searchQuery.trim()) return;

    const normalizedName = normalizeName(searchQuery);
    if (normalizedName.length < 2) {
      toast.error(t('validation.requester_name_too_short', 'Requester name must be at least 2 characters'));
      return;
    }

    try {
      setLoading(true);
      const newRequester = await createOrGetRequester(clientId, searchQuery.trim());
      
      // Add to local cache optimistically
      const cacheKey = `${clientId}-`;
      if (cache.has(cacheKey)) {
        const existingOptions = cache.get(cacheKey);
        const updatedOptions = [newRequester, ...existingOptions].slice(0, 10);
        cache.set(cacheKey, updatedOptions);
      }
      
      setSearchQuery(newRequester.name);
      setIsOpen(false);
      if (onChange) {
        onChange(newRequester.name);
      }
      
      toast.success(t('success.requester_created', 'Service requester added'));
    } catch (error) {
      console.error('Error creating requester:', error);
      toast.error(t('errors.create_requester_failed', 'Failed to create service requester'));
    } finally {
      setLoading(false);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Clear the field (does not delete the saved requester from the client list)
  const handleClear = () => {
    setSearchQuery('');
    setHighlightedIndex(-1);
    if (onChange) onChange('');
    inputRef.current?.focus();
  };

  // Delete a saved requester from the client's list
  const handleDeleteOption = async (e, option) => {
    e.preventDefault();
    e.stopPropagation();
    if (!clientId || !option?.id) return;
    if (!window.confirm(t('shipments.delete_requester_confirm', 'Delete this service requester?'))) return;
    try {
      await deleteRequester(clientId, option.id);
      setOptions(prev => prev.filter(o => o.id !== option.id));
      // Drop any cached entries for this client so the deleted option doesn't reappear
      setCache(new Map());
      if (value && normalizeName(value) === normalizeName(option.name)) {
        if (onChange) onChange('');
        setSearchQuery('');
      }
      toast.success(t('shipments.requester_deleted', 'Service requester deleted'));
    } catch (error) {
      console.error('Error deleting requester:', error);
      toast.error(t('errors.delete_requester_failed', 'Failed to delete service requester'));
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required}
          className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
          placeholder={disabled ? t('shipments.select_client_first', 'Select client first') : t('shipments.service_requester_placeholder', 'Type to search or add new...')}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
          {!disabled && searchQuery && !loading && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClear}
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              title={t('common.clear', 'Clear')}
              aria-label={t('common.clear', 'Clear')}
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          {loading ? (
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div 
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {options.length === 0 && !loading && !shouldShowAddOption() && (
            <div className="px-3 py-1.5 text-sm text-gray-500">
              {searchQuery ? t('shipments.no_requesters_found', 'No requesters found') : t('shipments.no_requesters', 'No requesters yet')}
            </div>
          )}

          {options.map((option, index) => (
            <div
              key={option.id}
              className={`flex items-center justify-between hover:bg-gray-100 ${
                index === highlightedIndex ? 'bg-gray-100' : ''
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <button
                type="button"
                className="flex-1 px-3 py-1.5 text-left focus:outline-none"
                onClick={() => selectOption(option)}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="text-sm font-medium text-gray-900">
                  {option.name}
                </div>
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => handleDeleteOption(e, option)}
                className="p-1 mr-2 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                title={t('common.delete', 'Delete')}
                aria-label={t('common.delete', 'Delete')}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}

          {shouldShowAddOption() && (
            <button
              type="button"
              className={`w-full px-3 py-1.5 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-t border-gray-200 ${
                highlightedIndex === options.length ? 'bg-blue-50' : ''
              }`}
              onClick={handleCreateNew}
              onMouseEnter={() => setHighlightedIndex(options.length)}
            >
              <div className="flex items-center text-sm font-medium text-blue-600">
                <PlusIcon className="w-4 h-4 mr-2" />
                {t('shipments.add_requester', 'Add "{{name}}"', { name: searchQuery })}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceRequesterCombobox;